import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import jsQR from 'jsqr';
import { useEffect, useRef, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  videoWrapper: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    backgroundColor: '#000',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  videoOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  },
  canvas: {
    display: 'none',
  },
  helperText: {
    marginTop: theme.spacing(1.5),
    textAlign: 'center',
  },
}));

// The QR code holds a link; the test code lives in its `code` query variable.
// Hash-routed links (e.g. https://host/#/page?code=X) keep the query inside the hash.
export const extractCodeFromQrContent = (content) => {
  try {
    const url = new URL(content);
    const fromSearch = url.searchParams.get('code');
    if (fromSearch) return fromSearch;
    const hashQueryIndex = url.hash.indexOf('?');
    if (hashQueryIndex >= 0) {
      return new URLSearchParams(url.hash.slice(hashQueryIndex + 1)).get('code');
    }
  } catch (err) {
    // Not a valid URL
  }
  return null;
};

export function QrCodeScannerDialog({ open, onClose, onScan }) {
  const classes = useStyles();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    if (!open) return undefined;

    let stream = null;
    let frameId = null;
    let cancelled = false;
    setError(null);
    setStarting(true);

    const scanFrame = () => {
      if (cancelled) return;
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qr = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'dontInvert',
        });
        if (qr?.data) {
          const code = extractCodeFromQrContent(qr.data);
          if (code) {
            onScan(code);
            return;
          }
          setError('QR Code inválido: o código da avaliação não foi encontrado.');
        }
      }
      frameId = requestAnimationFrame(scanFrame);
    };

    if (!navigator.mediaDevices?.getUserMedia) {
      setStarting(false);
      setError('Seu navegador não suporta acesso à câmera.');
      return undefined;
    }

    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((mediaStream) => {
        if (cancelled) {
          mediaStream.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = mediaStream;
        const video = videoRef.current;
        video.srcObject = mediaStream;
        return video.play().then(() => {
          setStarting(false);
          frameId = requestAnimationFrame(scanFrame);
        });
      })
      .catch(() => {
        if (cancelled) return;
        setStarting(false);
        setError('Não foi possível acessar a câmera. Verifique as permissões do navegador.');
      });

    return () => {
      cancelled = true;
      if (frameId) cancelAnimationFrame(frameId);
      if (stream) stream.getTracks().forEach((track) => track.stop());
    };
  }, [open, onScan]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Ler QR Code</DialogTitle>
      <DialogContent>
        <Box className={classes.videoWrapper}>
          <video ref={videoRef} className={classes.video} muted playsInline />
          {starting && !error && (
            <Box className={classes.videoOverlay}>
              <CircularProgress color="inherit" />
            </Box>
          )}
        </Box>
        <canvas ref={canvasRef} className={classes.canvas} />
        <Typography
          variant="body2"
          color={error ? 'error' : 'textSecondary'}
          className={classes.helperText}
        >
          {error || 'Aponte a câmera para o QR Code impresso na avaliação.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}
